import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { compressionCodecType, Dataset, LinkedService, Metamodel, Named, Owned, ParquetDataset } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { ParquetInlineSettings } from 'app/model/dataflow/dataset-settings/parquet-inline-settings';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { IDataMapperFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class ParquetDatasetTypeHandler extends DatasetTypeHandler {
    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('ParquetInlineSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as ParquetInlineSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as ParquetInlineSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as ParquetDataset)?.location?.getLocation()?.container;
                if (!containerPath) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as ParquetDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);

        });

        return tables;
    }

    public getFileOptions(): IDataMapperFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.zlib,
                    value: compressionCodecType.zlib
                },
                {
                    label: compressionCodecType.lzo,
                    value: compressionCodecType.lzo
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                }
            ]
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const parquetInlineSettings: ParquetInlineSettings = Owned.create<ParquetInlineSettings>(ParquetInlineSettings.MODEL, dataFlowSource);
        parquetInlineSettings.initialize(undefined);
        return parquetInlineSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const compressionCodec = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);
        const fileName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileName);
        const fileMode = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileMode);
        const fileSystem = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.fileSystem);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const wildcardPaths = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.wildcardPaths);

        // Create and populate ParquetInlineSettings
        const parquetSettings = Owned.create<ParquetInlineSettings>(ParquetInlineSettings.MODEL, table as DataFlowSource);
        parquetSettings.initialize(undefined);

        if (wildcardPaths && wildcardPaths.length > 0) {
            table.wildcardPaths = wildcardPaths.map((wp: string) => createDataflowExpression(wp) as DataFlowExpression);
        }

        parquetSettings.compressionType = compressionCodec ?? undefined;
        parquetSettings.container = createDataflowExpression(fileSystem) as DataFlowExpression;
        parquetSettings.fileName = createDataflowExpression(fileName) as DataFlowExpression;
        parquetSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        parquetSettings.fileMode = fileMode;

        return parquetSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Parquet, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add parquet dataset settings
        const parquetInlineSettings = selectedSourceFileOptions as ParquetInlineSettings;
        if (this.isSource && !!parquetInlineSettings.compressionType) {
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, parquetInlineSettings.compressionType);
        }
    }
}
